'use client';

import { ExpandButton } from '@/components/_rebrand/ExpandButton';
import ThemeWrapper from '@/components/_rebrand/ThemeWrapper';
import gtm from '@/lib/gtm';
import { ThemeNameType } from '@/tailwind-config/theme.constants';
import cn from 'classnames';
import { AnimatePresence, easeInOut, motion } from 'framer-motion';
import Image from 'next/image';
import { FC, PropsWithChildren, useEffect, useRef, useState } from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import { ModalContainer } from './ModalContainer';

type ModalProps = PropsWithClassName & PropsWithChildren & {
  name: string;
  id?: string;
  onClose?: () => void;
  preventClose?: boolean;
  sendEvents?: boolean;
  buttonTheme?: ThemeNameType;
  backgroundImageSrc?: string;
}

export const Modal: FC<ModalProps> = ({
  children,
  onClose,
  name,
  className,
  id,
  preventClose,
  sendEvents = true,
  buttonTheme = 'theme-white',
  backgroundImageSrc,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  // Internal visibility state for running exit animation
  const [isVisible, setIsVisible] = useState(true);

  /* Run this only once for GTM tracking */
  useEffect(() => {
    if (sendEvents) {
      gtm.track('modal_opened', {
        modal_label: name,
      });
    }
  }, []);

  // Escape key listener to close the modal
  useEffect(() => {
    const handleEscapeKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        !preventClose && onModalClose();
      }
    };

    window.addEventListener('keydown', handleEscapeKeyDown);

    // Cleanup function
    return () => {
      window.removeEventListener('keydown', handleEscapeKeyDown);
    };
  }, []);

  /*
   * Closing modal takes a few steps:
   * 1. Send the GTM event
   * 2. Setting isVisible to false triggers the exit animation
   * 3. AnimatePresence' onExitComplete callback fires, which calls the onClose callback
   */
  const onModalClose = () => {
    if (sendEvents) {
      gtm.track('modal_closed', {
        modal_label: name,
      });
    }

    // Trigger the exit animation; onClose fires from onExitComplete below.
    setIsVisible(false);
  };

  const handleExitComplete = () => {
    if (!isVisible && onClose) onClose();
  };

  // Use more transparent background filter when using a background image. 
  const backdropFilterClass = backgroundImageSrc ? ' bg-black/20' : 'bg-black/80';

  return (
    <AnimatePresence onExitComplete={handleExitComplete}>
      {isVisible && (
        <RemoveScroll>
          {/* Outer wrapper has 2147483643 z-index - this exact number is important!!
              It must be higher than the Qualified chat's z-index of 2147483642,
              but lower than the Onetrust modal's z-index of 2147483646.
              These numbers all approach the maximum possible z-index of value 2147483647. */}
          <motion.div
            key="modal-overlay"
            onClick={() => !preventClose && onModalClose()}
            className="fixed inset-0 overflow-hidden touch-none z-[2147483643]"
            initial={{ opacity: 0, }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              ease: easeInOut,
              duration: 400 / 1000,
            }}
          >
            {backgroundImageSrc && (
              <div className="absolute inset-0 z-0">
                <div className="hidden md:block">
                  <Image
                    className="object-cover w-full h-full"
                    src={backgroundImageSrc}
                    alt="Modal Full Screen Overlay"
                    width={1200}
                    height={1200}
                  />
                </div>
              </div>
            )}
            {/* Modal backdrop - clicking here should close the modal */}
            <div
              id={id}
              ref={ref}
              className={cn('absolute inset-0 backdrop-blur-[3px] ', backdropFilterClass)}
            />
            {/* Scrollable wrapper */}
            <div className="flex items-center relative h-[100dvh] overflow-y-scroll p-2">
              <motion.div
                className="m-auto"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{
                  ease: easeInOut,
                  duration: 400 / 1000,
                }}
                // Stop the onClick listener from firing in the modal
                onClick={(e) => e.stopPropagation()}
              >
                <ModalContainer className={className}>
                  {!preventClose && (
                    <div className="absolute top-0 right-0 p-xs z-10">
                      <div className="origin-center transform rotate-45">
                        <ThemeWrapper theme={buttonTheme}>
                          <ExpandButton parentSurfaceClass="surface-l0" size="large" onClick={onModalClose} isExpanded={false} />
                        </ThemeWrapper>
                      </div>
                    </div>
                  )}
                  {children}
                </ModalContainer>
              </motion.div>
            </div>
          </motion.div>
        </RemoveScroll>
      )
      }
    </AnimatePresence >
  );
};

export default Modal;
